/**
 * Instant in-memory filter for the Buy Requests list (Active + Past tabs).
 * Text search is fuzzy over what the buyer wrote and the parsed criteria;
 * category pills are the umbrella product categories (DB-owned, via
 * productFormData) present in the list — specific grades are found by typing.
 */
import { fuzzyFilter } from '@/lib/fuzzyMatch';
import { LEGACY_PRODUCT_FORMS, PRODUCT_FORM_CATEGORIES, PRODUCT_FORM_ITEMS } from '@/lib/productFormData';

export type FilterableRequest = {
  raw_text: string;
  strain_query: string | null;
  product_form: string | null;
  species?: string | null;
  environment?: string | null;
  matchCount: number;
};

/** Umbrella category for a product form, or null when unknown. */
export const categoryLabel = (form: string | null): string | null => {
  if (!form) return null;
  const v = form.trim().toLowerCase();
  const hit = PRODUCT_FORM_ITEMS.find((i) => i.value === v) ?? LEGACY_PRODUCT_FORMS.find((i) => i.value === v);
  return hit ? hit.category : null;
};

/** Distinct umbrella categories present, in canonical category order. */
export function requestCategories(items: FilterableRequest[]): string[] {
  const present = new Set(items.map((i) => categoryLabel(i.product_form)).filter((x): x is string => !!x));
  return PRODUCT_FORM_CATEGORIES.filter((c) => present.has(c));
}

export function filterBuyRequests<T extends FilterableRequest>(
  items: T[],
  opts: { query: string; category: string | null; hasMatches: boolean },
): T[] {
  const narrowed = items.filter(
    (i) => (!opts.category || categoryLabel(i.product_form) === opts.category) && (!opts.hasMatches || i.matchCount > 0),
  );
  return fuzzyFilter(narrowed, opts.query, (i) => [i.raw_text, i.strain_query, i.product_form, i.species, i.environment]);
}
